const {
	AJAX_TIMEOUT,
	getSelectionValue,
	getCheckboxSelectedValues,
	getRadioSelectedValue,
	toggleClass,
	hasClass,
	onClickOrEnter,
	isValidEmail,
	runAjax,
	logMarketingEvent,
	scrollToEl,
} = require("./ElevHelpers");
const {setupModalButton} = require("./ModalUtil");
const ElevShared = require("../../../../shared/ElevShared");
const ElevHelpers = require("./ElevHelpers");
const QueryString = require("query-string");

// valid audiences
const ALLOWED_AUDIENCES = ["parent", "teacher"];
let sortBy = "recommended";
// Initially only show 12
const INITIALLY_SHOWN = 12;
// How many more activities to load when load more clicked
const LOAD_MORE = 6;
// Show sticky at this scroll position, when not mobile
const SHOW_STICKY_AT = 650;
let hideSticky = false;
// Keeping track of applied filter states
let filterValues = {
	age: [],
	type: [],
	players: "",
	location: [],
	time: "",
	goals: [],
};
const HIDDEN_CLASS = "--hidden";
// show other activities if the total amount of filtered activities is less than this number
const OTHER_ACTIVITIES_LIMIT = 6;
// show this many other activities if they need to be shown
const NUM_OTHER_ACTIVITIES = 9;
// width of scrollbars
let scrollbarWidth = 0;

/**
 * Outputs whether the string value "val" is in the string value "range".
 * Possible range formats: "X", "X+", "X-Y"
 *
 * @param   {String}  val    Value to be checked. Ex. "3", "10+"
 * @param   {String}  range  Given range of values, ex. "4-6", "4+", or just a number, ex. "4"
 *
 * @return  {Boolean} True if val is within range
 */
const isWithinRange = (val, range) => {
	if (!val || !range) return false;
	val = val.toString();
	range = range.toString();
	if (range.includes("+")) {
		if (isNaN(val) && val.includes("+")) {
			return true;
		}
		return parseInt(val) >= parseInt(range);
	}
	if (range.includes("-")) {
		const startEnd = range.split("-").map(i => parseInt(i.trim()));
		if (isNaN(val) && val.includes("+")) {
			return parseInt(val) <= startEnd[1];
		}
		return parseInt(val) >= startEnd[0] && parseInt(val) <= startEnd[1];
	}
	return val === range;
};

/**
 * Turns a range, like 2-4 or 4+ into a list like [2, 3, 4] or [4, 5, 6, ... 11]
 * List goes up to 11.
 *
 * @param   {String}  range  value like "2-4" or "4+"
 *
 * @return  {Array|Integer}  value like [2, 3, 4] or [4, 5, 6, ... 11]
 */
const rangeToList = range => {
	const list = [];
	let startEnd;
	if (range.includes("-")) {
		startEnd = range.split("-").map(i => parseInt(i.trim()));
	} else {
		startEnd = [parseInt(range), 11];
	}
	for (let v = startEnd[0]; v <= startEnd[1]; v++) {
		list.push(v);
	}
	return list;
};

/**
 * Returns if the contents of two arrays are identical
 * The arrays are copied and sorted, then compared element-by-element
 *
 * @param {Array} first
 * @param {Array} second
 * @returns {Boolean} true if arrays contain identical values, false otherwise
 */
const areArraysEqual = (arr1, arr2) => {
	if (arr1.length !== arr2.length) {
		return false;
	}
	const arr1Copy = [...arr1].sort();
	const arr2Copy = [...arr2].sort();

	for (let i = 0; i < arr1Copy.length; ++i) {
		if (arr1Copy[i] !== arr2Copy[i]) {
			return false;
		}
	}

	return true;
};

/**
 * Outputs if two values are equal
 *
 * @param {Object} compare1
 * @param {Object} compare2
 * @returns {Boolean} true if both values are equal, false otherwise
 */
const areValuesEqual = (value1, value2) => {
	if (Array.isArray(value1) && Array.isArray(value2)) {
		return areArraysEqual(value1, value2);
	}
	return value1 === value2;
};

// Same as isWithinRange, but takes in multiple elements in "vals" and returns
// true if any of the "vals" are within "range".
const anyWithinRange = (vals, range) => {
	vals = Array.isArray(vals) ? vals : [vals];
	return vals.some(v => isWithinRange(v, range));
};

const isEmpty = val => {
	if (Array.isArray(val)) return val.length === 0;
	else return val === "" || val === undefined;
};

// helper because some older browsers don't have Array.prototype.flat()
const flatten = arr => Array.prototype.concat(...arr);

const getActivityURL = () => {
	const urlParts = window.location.pathname.split("/");
	return urlParts[urlParts.length - 1];
};

// get total number of filters
const getNumFilters = (filterables = filterValues) => {
	const values = Object.values(filterables);
	return flatten(values).filter(f => !isEmpty(f)).length;
};

// get total number of categories of filters
const getNumFilterCategories = (filterables = filterValues) => {
	const values = Object.values(filterables);
	return values.filter(f => !isEmpty(f)).length;
};

const filterActivities = function(filterables) {
	const filterBy = filterables || filterValues;
	const playgroundActivities = window.playgroundActivitiesData;
	// Fisher-Yates shuffle, taken from https://dev.to/codebubb/how-to-shuffle-an-array-in-javascript-2ikj
	const shuffleArray = array => {
		for (let i = array.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			const temp = array[i];
			array[i] = array[j];
			array[j] = temp;
		}
		return array;
	};
	const numCategories = getNumFilterCategories(filterBy);
	if (numCategories === 0) {
		return {
			exactMatches: playgroundActivities,
			closeMatches: [],
			otherActivities: [],
			numCategories,
		};
	}
	const activitiesWithMatched = playgroundActivities.map(a => {
		let matchCount = 0;
		["age", "players"].forEach(f => {
			if (!isEmpty(filterBy[f])) {
				if (anyWithinRange(filterBy[f], a[f])) matchCount++;
			}
		});
		["type", "time", "goals", "location"].forEach(f => {
			if (!isEmpty(filterBy[f])) {
				if (Array.isArray(filterBy[f])) {
					// type, goals, location (checkboxes)
					if (Array.isArray(a[f])) {
						const intersection = filterBy[f].filter(fv => a[f].includes(fv));
						if (intersection.length) matchCount++;
					} else if (
						// no "location" on activity = play anywhere
						(f === "location" && !a[f]) ||
						filterBy[f].includes(a[f])
					) {
						matchCount++;
					}
				} else {
					// time
					if (
						filterBy[f] === a[f] ||
						filterBy[f] === a[f].toString().toLowerCase()
					)
						matchCount++;
				}
			}
		});
		a.matchCount = matchCount;
		return a;
	});

	const exactMatches = activitiesWithMatched.filter(
		a => a.matchCount === numCategories
	);
	const closeMatches = activitiesWithMatched
		.filter(a => a.matchCount > 0 && a.matchCount < numCategories)
		.sort((a, b) => b.matchCount - a.matchCount);
	const otherActivities =
		exactMatches.length + closeMatches.length <= OTHER_ACTIVITIES_LIMIT
			? shuffleArray(
					playgroundActivities.filter(a => a.matchCount === 0)
			  ).slice(-NUM_OTHER_ACTIVITIES)
			: [];

	return {
		exactMatches,
		closeMatches,
		otherActivities,
		numCategories,
	};
};

const markFilteredCards = (gridEl, matches) => {
	if (!gridEl) return;
	if (!matches || !matches.length) return;

	const activityCards = gridEl.querySelectorAll(".pg-video-card-container");
	const activities = matches.map(a => a.url);
	activityCards.forEach(card => {
		toggleClass(card, "matches", activities.includes(card.dataset.name));
	});
};

const doFilter = () => {
	const {
		exactMatches,
		closeMatches,
		otherActivities,
		numCategories,
	} = filterActivities();

	const sortEl = document.querySelector(".pg-sort-wrapper");
	const mainContainer = document.querySelector(".pg-video-grid-container-all");
	const closeContainer = document.querySelector(
		".pg-video-grid-container-close"
	);
	const otherContainer = document.querySelector(
		".pg-video-grid-container-other"
	);
	const mainGrid = mainContainer.querySelector(".pg-videos-grid");
	const closeGrid = closeContainer.querySelector(".pg-videos-grid");
	const otherGrid = otherContainer.querySelector(".pg-videos-grid");
	const mainLabel = mainContainer.querySelector(".pg-sort-label");
	const noMainLabel = mainContainer.querySelector(".pg-no-match-label");
	const generalLoadMoreButton = mainContainer.querySelector(".pg-load-more");
	const exactFillerCard = mainGrid.querySelector(
		".pg-video-card-container-filler"
	);
	const otherFillerCard = otherGrid.querySelector(
		".pg-video-card-container-filler"
	);
	markFilteredCards(mainGrid, exactMatches);
	markFilteredCards(closeGrid, closeMatches);

	if (numCategories === 0) {
		const generalAllLoaded =
			window.playgroundActivitiesData.length <=
			parseInt(mainGrid.dataset.numShown);
		toggleClass(mainGrid, HIDDEN_CLASS, false);
		toggleClass(mainLabel, HIDDEN_CLASS, true);
		toggleClass(noMainLabel, HIDDEN_CLASS, true);
		toggleClass(closeContainer, HIDDEN_CLASS, true);
		toggleClass(otherContainer, HIDDEN_CLASS, true);
		toggleClass(sortEl, HIDDEN_CLASS, false);
		toggleClass(generalLoadMoreButton, HIDDEN_CLASS, generalAllLoaded);
		// For alignment purposes, show the filler video card
		toggleClass(
			exactFillerCard,
			HIDDEN_CLASS,
			generalAllLoaded && generalAllLoaded % 3 !== 2
		);
		displayCards(exactMatches, mainGrid);
		showXActivities(mainContainer, true);
	} else {
		toggleClass(generalLoadMoreButton, HIDDEN_CLASS, true);
		if (exactMatches.length) {
			toggleClass(mainGrid, HIDDEN_CLASS, false);
			toggleClass(mainLabel, HIDDEN_CLASS, false);
			toggleClass(noMainLabel, HIDDEN_CLASS, true);
			toggleClass(sortEl, HIDDEN_CLASS, false);
			displayCards(exactMatches, mainGrid);
			showXActivities(mainContainer, true);
			// For alignment purposes, show the filler video card
			toggleClass(exactFillerCard, HIDDEN_CLASS, exactMatches.length % 3 !== 2);
		} else {
			toggleClass(mainGrid, HIDDEN_CLASS, true);
			toggleClass(mainLabel, HIDDEN_CLASS, true);
			toggleClass(noMainLabel, HIDDEN_CLASS, false);
			// Also hide sort since you can't sort close matches
			toggleClass(sortEl, HIDDEN_CLASS, true);
		}
		if (closeMatches.length) {
			toggleClass(closeContainer, HIDDEN_CLASS, false);
			reorderCards(closeMatches, closeGrid);
			displayCards(closeMatches, closeGrid);
			showXActivities(closeContainer, true);
		} else {
			toggleClass(closeContainer, HIDDEN_CLASS, true);
		}
		if (otherActivities.length) {
			toggleClass(otherContainer, HIDDEN_CLASS, false);
			reorderCards(otherActivities, otherGrid);
			displayCards(otherActivities, otherGrid);
			toggleClass(otherFillerCard, HIDDEN_CLASS, closeMatches.length % 3 !== 2);
		} else {
			toggleClass(otherContainer, HIDDEN_CLASS, true);
		}
	}
};

const getAudience = () => {
	return window.sessionStorage.getItem("baPlaygroundAudience") || "parent";
};

const getInactiveAudiences = () => {
	const audience = getAudience();
	return ALLOWED_AUDIENCES.filter(a => a !== audience);
};

const saveAudience = audience => {
	return window.sessionStorage.setItem("baPlaygroundAudience", audience);
};

// Update recommended videos on single activity page
const updateRecs = () => {};

const doSort = () => {
	const mainContainer = document.querySelector(".pg-video-grid-container-all");
	if (!mainContainer) return;
	const mainGrid = mainContainer.querySelector(".pg-videos-grid");
	if (!mainGrid) return;

	const order = getAudience() === "parent" ? "parentOrder" : "teacherOrder";
	const sortParams = [order];
	if (sortBy !== "recommended") {
		sortParams.unshift(sortBy);
	}
	const sortFirst = sortParams[0];
	const sortSecond = sortParams[1];
	const sortedActivities = window.playgroundActivitiesData.sort((a, b) => {
		if (a[sortFirst] < b[sortFirst]) return -1;
		if (a[sortFirst] > b[sortFirst]) return 1;
		if (sortSecond) {
			return a[sortSecond] <= b[sortSecond] ? -1 : 1;
		}
		return 0;
	});

	reorderCards(sortedActivities, mainGrid);
	showXActivities(mainContainer);
};

const reorderCards = (ordered, parentEl) => {
	const activityCards = parentEl.querySelectorAll(".pg-video-card-container");
	activityCards.forEach(ac => {
		let index;
		for (let i = 0; i < ordered.length; i++) {
			if (ordered[i].url === ac.dataset.name) {
				index = i;
				break;
			}
		}
		ac.style.order = index + 1;
	});
};

const displayCards = (shown, parentEl) => {
	const activityCards = parentEl.querySelectorAll(".pg-video-card-container");
	const shownActivities = shown.map(s => s.url);
	activityCards.forEach(ac => {
		toggleClass(ac, HIDDEN_CLASS, !shownActivities.includes(ac.dataset.name));
	});
};

// Sets the filters and syncs them across filters
const applyFilters = function(formEl, isClear) {
	const advForm = document.querySelector(".pg-form-advanced");
	const ageForm = document.querySelector(".pg-form-age");
	const typeForm = document.querySelector(".pg-form-type");

	const oldFilterValues = {...filterValues};

	if (hasClass(formEl, "pg-form-age")) {
		if (isClear) {
			filterValues.age = [];
			syncFilterTo(formEl);
		} else {
			filterValues.age = getCheckboxSelectedValues("pg-age-input", ageForm);
		}
		// Here we only want to send out an event if the filters in the list were changed
		// We don't want to send out an event if a user just opened the filters but didn't
		// change any of them
		if (
			filterValues.age &&
			filterValues.age.length &&
			!areArraysEqual(oldFilterValues.age, filterValues.age)
		) {
			ElevHelpers.sendDataLayerEvent("filterByAge", {
				age: filterValues.age,
			});
		}
		syncFilterTo(advForm);
	} else if (hasClass(formEl, "pg-form-type")) {
		if (isClear) {
			filterValues.type = [];
			syncFilterTo(formEl);
		} else {
			filterValues.type = getCheckboxSelectedValues("pg-type-input", typeForm);
		}
		// The same thing as before, we only want to fire an event if the filters changed
		if (
			filterValues.type &&
			filterValues.type.length &&
			!areArraysEqual(oldFilterValues.type, filterValues.type)
		) {
			ElevHelpers.sendDataLayerEvent("filterByActivityType", {
				type: filterValues.type,
			});
		}
		syncFilterTo(advForm);
	} else {
		// advanced form
		if (isClear) {
			filterValues = {
				age: [],
				type: [],
				players: "",
				location: [],
				time: "",
				goals: [],
			};
			syncFilterTo(formEl);
		} else {
			const prevFilterValues = {...filterValues};
			filterValues = {
				age: getCheckboxSelectedValues("pg-age-input", advForm),
				type: getCheckboxSelectedValues("pg-type-input", advForm),
				players: getSelectionValue("#pg-players-input"),
				location: getCheckboxSelectedValues("pg-location-input"),
				time: getSelectionValue("#pg-time-input"),
				goals: getCheckboxSelectedValues("pg-goals-input"),
			};
			// Filters start off empty but applyFilters is called on page load,
			// so make sure we aren't firing this event on page loads
			// If any of these are not empty, then send the advanced filter event
			Object.keys(filterValues).forEach(filter => {
				const values = filterValues[filter];
				const oldValues = prevFilterValues[filter];

				const areSame = areValuesEqual(values, oldValues);

				if (values && values.length > 0 && !areSame) {
					const eventName =
						"filterBy" + filter.charAt(0).toUpperCase() + filter.slice(1);
					ElevHelpers.sendDataLayerEvent(eventName, {[filter]: values});
				}
			});
		}
		syncFilterTo(ageForm);
		syncFilterTo(typeForm);
	}
	updateFilterLabels();
	doFilter();
};

const updateFilterLabels = () => {
	const advLabel = document.querySelector("#pg-adv-filter-label");
	const ageLabel = document.querySelector("#pg-age-filter-label");
	const typeLabel = document.querySelector("#pg-type-filter-label");
	const numFilters = getNumFilters();
	advLabel.textContent = numFilters > 0 ? `Filters (${numFilters})` : "Filters";
	if (filterValues.age.length > 0) {
		ageLabel.textContent = `Ages (${filterValues.age.length})`;
	} else {
		ageLabel.textContent = "Any Age";
	}
	if (filterValues.type.length > 0) {
		typeLabel.textContent = `Activity Type${
			filterValues.type.length > 1 ? "s" : ""
		} (${filterValues.type.length})`;
	} else {
		typeLabel.textContent = "All Activity Types";
	}
};

// Make sure that filter values are synced with checkboxes in the
// toFormEl (form element)
const syncFilterTo = function(toFormEl) {
	const checkboxInputs = ["age", "type", "location", "goals"];
	checkboxInputs.forEach(f => {
		const inputs = toFormEl.querySelectorAll(`input[name="pg-${f}-input"]`);
		if (inputs) {
			inputs.forEach(a => {
				a.checked = filterValues[f].includes(a.value);
			});
		}
	});
	const selectionInputs = ["time", "players"];
	selectionInputs.forEach(f => {
		const input = toFormEl.querySelector(`#pg-${f}-input`);
		if (input) {
			input.value = filterValues[f];
		}
	});
};

// hide/show/change views depending on the audience selected
const updateViewsForAudience = () => {
	updateButtons();
	updateBanner();

	// update for views that don't need special show/hide logic
	const activeViews = document.querySelectorAll(`.pg-${getAudience()}`);
	const inactiveViews = getInactiveViews("pg");
	activeViews.forEach(view => {
		toggleClass(view, HIDDEN_CLASS, false);
	});
	inactiveViews.forEach(view => {
		toggleClass(view, HIDDEN_CLASS, true);
	});
};

const updateButtons = () => {
	const audience = getAudience();

	// Show the right audience button selected
	const activeButtons = document.querySelectorAll(`.pg-button-${audience}`);
	const inactiveButtons = getInactiveViews("pg-button");

	activeButtons.forEach(function(b) {
		toggleClass(b, "active", true);
	});
	inactiveButtons.forEach(function(b) {
		toggleClass(b, "active", false);
	});
};

const updateBanner = () => {
	const audience = getAudience();
	const inactiveAudiences = getInactiveAudiences();

	// update for desktop widths
	const banner = document.querySelector(".pg-top-banner");
	if (banner) {
		// add class for correct audience
		toggleClass(banner, `pg-top-banner-${audience}`, true);
		// remove classes for any incorrect audiences
		inactiveAudiences.forEach(inactive => {
			toggleClass(banner, `pg-top-banner-${inactive}`, false);
		});
	}
};

// helper function to get all the views for all the inactive audiences
const getInactiveViews = classPrefix => {
	const inactiveAudiences = getInactiveAudiences();
	const inactiveViews = [];

	inactiveAudiences.forEach(inactive => {
		const views = document.querySelectorAll(`.${classPrefix}-${inactive}`);
		if (views.length) {
			inactiveViews.push(...views);
		}
	});

	return inactiveViews;
};

const setAudience = function(audience) {
	const playgroundAudience = getAudience();
	if (playgroundAudience === audience) return;
	saveAudience(audience);
	ElevHelpers.sendDataLayerEvent("setAudience", {audience});

	// Set the right views for audience
	updateViewsForAudience();

	// Update videos
	const hasRec = document.querySelector(".pg-rec-videos");
	if (hasRec) updateRecs();
	else doSort();
};

const toggleFilter = function(evt, el, active) {
	evt.stopPropagation();

	const backdrop = document.querySelector(".pg-filter-form-modal-bg");
	// Deal with current filter
	toggleClass(backdrop, "active", active);
	if (
		hasClass(el, "pg-filter-button-advanced") ||
		hasClass(el, "pg-form-advanced")
	) {
		toggleClass(el, "active", active);
		toggleClass(backdrop, "dark", active);
		const filterForm = document.querySelector(".pg-form-advanced");
		toggleClass(filterForm, "active", active);
		// prevent body from scrolling when modal is active
		setBodyScrolling(!active);
	} else {
		toggleClass(backdrop, "dark", false);
		toggleClass(el, "active", active);
	}
};

const setupFilters = () => {
	const filterButtons = document.querySelectorAll(".pg-filter-button");
	filterButtons.forEach(function(b) {
		b.addEventListener("click", evt => toggleFilter(evt, b, true));
		const clearButton = b.querySelector(".pg-filter-clear-button");
		const doneButton = b.querySelector(".pg-filter-done-button");
		const formEl = b.querySelector(".pg-filter-form");
		if (clearButton) {
			clearButton.addEventListener("click", evt => {
				toggleFilter(evt, b, false);
				applyFilters(formEl, true);
			});
		}
		if (doneButton) {
			doneButton.addEventListener("click", evt => {
				toggleFilter(evt, b, false);
				applyFilters(formEl);
			});
		}
	});
	// The advanced form is not within the button, so separate selectors used here
	const advClear = document.querySelector(
		".pg-form-advanced .pg-filter-clear-button"
	);
	const advDone = document.querySelector(
		".pg-form-advanced .pg-filter-done-button"
	);
	const advClose = document.querySelector(".pg-form-advanced .pg-filter-close");
	const advButton = document.querySelector(".pg-filter-button-advanced");
	const advForm = document.querySelector(".pg-form-advanced");

	if (advClear) {
		advClear.addEventListener("click", evt => {
			toggleFilter(evt, advButton, false);
			applyFilters(advForm, true);
		});
	}
	if (advDone) {
		advDone.addEventListener("click", evt => {
			toggleFilter(evt, advButton, false);
			applyFilters(advForm);
		});
	}
	if (advClose) {
		advClose.addEventListener("click", evt => {
			toggleFilter(evt, advButton, false);
			applyFilters(advForm);
		});
	}

	// Close all filters when outside click filters
	const modalBg = document.querySelector(".pg-filter-form-modal-bg");
	if (modalBg) {
		modalBg.addEventListener("click", evt => {
			setBodyScrolling(true);
			toggleClass(modalBg, "active", false);
			const filterButton = document.querySelector(".pg-filter-button.active");
			const filterModal = document.querySelector(".pg-form-advanced.active");
			if (filterButton) {
				toggleClass(filterButton, "active", false);
				const form = filterButton.querySelector(".pg-filter-form");
				if (form) applyFilters(form);
			}
			if (filterModal) {
				toggleClass(filterModal, "active", false);
				applyFilters(filterModal);
			}
		});
	}

	if (advForm) {
		setTimeout(() => {
			// wait for browser cache then apply any cached filters
			applyFilters(advForm);
		}, 0);
	}
};

const showFillerIfNeeded = gridEl => {
	const filler = gridEl.querySelector(".pg-video-card-container-filler");
	if (filler) {
		// first hide filler
		toggleClass(filler, HIDDEN_CLASS, true);
		// then check if we need to show it
		const numCardsShown = gridEl.querySelectorAll(
			".pg-video-card-container:not(.--hidden)"
		).length;
		if (numCardsShown % 3 === 2) {
			toggleClass(filler, HIDDEN_CLASS, false);
		}
	}
};

// only show the number of activities in numShown
const showXActivities = (containerEl, reset = false) => {
	if (!containerEl) return;
	const gridEl = containerEl.querySelector(".pg-videos-grid");
	if (!gridEl) return;
	if (reset) gridEl.dataset.numShown = INITIALLY_SHOWN;

	const scrollPosition =
		document.documentElement.scrollTop || document.body.scrollTop;
	// convert to array to allow sorting
	const activityCards = Array.from(
		containerEl.querySelectorAll(".pg-video-card-container.matches")
	);
	// first sort matched cards by order
	activityCards.sort(
		(a, b) => parseInt(a.style.order) - parseInt(b.style.order)
	);
	// then show first numShown cards by index in sorted order
	activityCards.forEach((card, i) => {
		toggleClass(card, HIDDEN_CLASS, i >= parseInt(gridEl.dataset.numShown));
	});

	// something about displaying more cards shifts the user's scroll position, so let's reset it
	window.scrollTo(0, scrollPosition);

	// check load more button
	const loadMoreButton = containerEl.querySelector(".pg-load-more");
	toggleClass(
		loadMoreButton,
		HIDDEN_CLASS,
		activityCards.length <= parseInt(gridEl.dataset.numShown)
	);

	showFillerIfNeeded(gridEl);
};

const loadMoreActivities = containerEl => {
	if (!containerEl) return;
	const gridEl = containerEl.querySelector(".pg-videos-grid");
	if (!gridEl) return;

	gridEl.dataset.numShown = parseInt(gridEl.dataset.numShown) + LOAD_MORE;
	showXActivities(containerEl);
};

const onSubscribe = () => {
	const emailInput = document.querySelector(".pg-email-input");
	const email = emailInput.value.trim();

	if (!email || !isValidEmail(email)) return;
	const role = getRadioSelectedValue("playground-role");
	const generalEl = document.querySelector(
		`input[name="pg-general-mailinglist"]`
	);
	const consentEl = document.querySelector(`input[name="pg-signup-age"]`);
	const errorEl = document.querySelector(".modal-email-signup-error");
	consentEl.addEventListener("click", () =>
		toggleClass(errorEl, "ba11--invisible", true)
	);
	if (!consentEl.checked) {
		errorEl.innerHTML = ElevShared.copyForEmailSignupErrors("ageNotConfirmed");
		toggleClass(errorEl, "ba11--invisible", false);
	} else {
		return runAjax(
			{
				endpoint: "/api/mailingList/playgroundSubscribe",
				timeout: AJAX_TIMEOUT,
				payload: {email, role, addToGeneral: generalEl.checked},
			},
			showResponse
		);
	}
};

const setupEmailModal = modalEl => {
	const submitButtonEl = modalEl.querySelector(
		".modal-email-playground-signup"
	);
	if (submitButtonEl) onClickOrEnter(submitButtonEl, onSubscribe);
};

const toggleExpandable = function(el) {
	const isClosed = hasClass(el, "--closed");
	toggleClass(el, "--closed", !isClosed);
};

const showResponse = function(err, resp) {
	const errorEl = document.querySelector(".modal-email-signup-error");
	const successEl = document.querySelector(".modal-email-signup-success");
	const submitEl = document.querySelector(".modal-email-playground-signup");
	if (resp.error) {
		errorEl.innerHTML = ElevShared.copyForEmailSignupErrors(resp.error);
		toggleClass(errorEl, "ba11--invisible", false);
	} else if (err) {
		errorEl.innerHTML = ElevShared.copyForEmailSignupErrors("signUpFail");
		toggleClass(errorEl, "ba11--invisible", false);
	} else {
		markAsSubscribed();
		displaySignupStickyIfNecessary();
		toggleClass(errorEl, "ba11--invisible", true);
		toggleClass(submitEl, "ba11--hidden", true);
		toggleClass(successEl, "ba11--hidden", false);
		toggleClass(
			document.querySelector(".modal-email-signup-checkbox-label"),
			"modal-email-signup-disabled",
			true
		);
		document.querySelectorAll(".modal-email-signup-role").forEach(role => {
			role.disabled = true;
		});
		document
			.querySelectorAll(".modal-email-signup-checkbox")
			.forEach(checkbox => {
				checkbox.disabled = true;
			});
		if (resp.success && resp.subscribed) {
			if (resp.mailingListID) {
				logMarketingEvent(
					"PlaygroundMailingListSignUp",
					parseInt(resp.mailingListID)
				);
			}
			if (resp.generalMailingListID) {
				logMarketingEvent(
					"MailingListSignUp",
					parseInt(resp.generalMailingListID)
				);
			}
			if (global.dataLayer) {
				global.dataLayer.push({event: "joinPlaygroundMailingList"});
				if (resp.generalMailingListID) {
					global.dataLayer.push({event: "joinMailingList"});
				}
			}
		}
	}
};

const markAsSubscribed = () => {
	window.sessionStorage.setItem("baPlaygroundEmailSubscription", true);
};

const checkIfSubscribed = () => {
	return (
		window.sessionStorage.getItem("baPlaygroundEmailSubscription") || false
	);
};

const displaySignupStickyIfNecessary = () => {
	const signupSticky = document.querySelector(".pg-sticky-signup");
	const isSubscribed = checkIfSubscribed();
	const showSignupBar =
		signupSticky && // has signup sticky
		window.scrollY >= SHOW_STICKY_AT && // at appropriate scroll height
		!isSubscribed && // is not yet subscribed
		!hideSticky; // user hasn't closed the sticky
	if (signupSticky) {
		signupSticky.dataset.show = showSignupBar ? 1 : 0;
	}
	const bottomBar = document.querySelector(".ba11-bottom-bar-main");
	if (bottomBar) {
		const bottomBarExtraClass = "ba11-bottom-bar-extra";
		if (showSignupBar) {
			bottomBar.classList.add(bottomBarExtraClass);
		} else {
			bottomBar.classList.remove(bottomBarExtraClass);
		}
	}
};

const setupSignupSticky = () => {
	if (isPlaygroundActivityPage()) return;
	displaySignupStickyIfNecessary();

	document.addEventListener("scroll", () => {
		displaySignupStickyIfNecessary();
	});

	const closeSticky = document.querySelector(".pg-sticky-signup-close");
	if (closeSticky) {
		closeSticky.addEventListener("click", () => {
			hideSticky = true;
			displaySignupStickyIfNecessary();
		});
	}
};

// makes an off-screen scrolling element for us to reliably measure the width a scrollbar
const getScrollbarWidth = () => {
	const div = document.createElement("body");
	div.className = "scrollbar-measure";
	document.body.appendChild(div);

	scrollbarWidth = div.offsetWidth - div.clientWidth;

	document.body.removeChild(div);
};

const setBodyScrolling = scrollable => {
	const overflow = scrollable ? "auto" : "hidden";
	const paddingRight = scrollable ? "0" : `${scrollbarWidth}px`;

	const body = document.querySelector("body");
	// note that overflowY doesn't work because Safari doesn't like that
	body.style.overflow = overflow;
	body.style.paddingRight = paddingRight;
	const topbar = body.querySelector(".ba11-top");
	if (topbar) {
		const width = scrollable ? "100%" : `calc(100% - ${paddingRight})`;
		topbar.style.width = width;
	}
};

const setupRecVideos = function() {
	const activityURL = getActivityURL();
	let age, goals;
	for (let i = 0; i < window.playgroundActivitiesData.length; i++) {
		const activity = window.playgroundActivitiesData[i];
		if (activity.url === activityURL) {
			age = rangeToList(activity.age);
			goals = activity.goals;
			break;
		}
	}
	const {exactMatches, closeMatches} = filterActivities({age, goals});
	const recVideoURLs = [];
	//  Have first 2 recommended videos to select randomly from the first 15
	//  activities (according to our recommended order), and the last video to
	//  select randomly from the rest
	const recAct = exactMatches.concat(closeMatches);
	const recActSplit1 = recAct.slice(0, 15);
	const recActSplit2 = recAct.slice(15);
	let index = 0;
	while (recVideoURLs.length < 3) {
		const batch = recVideoURLs.length < 2 ? recActSplit1 : recActSplit2;
		const rand = Math.floor(Math.random() * batch.length);
		const a = recAct[rand];
		if (a.url !== activityURL && !recVideoURLs.includes(a.url)) {
			recVideoURLs.push(a.url);
		}
		index++;
		if (index > 100) break; // just in case;
	}
	const videoCards = document.querySelectorAll(".pg-video-card-container");
	videoCards.forEach(vc => {
		const name = vc.dataset.name;
		if (recVideoURLs.includes(name)) {
			toggleClass(vc, HIDDEN_CLASS, false);
			const order = recVideoURLs.indexOf(name) + 1;
			toggleClass(vc, `pga-rec-video-card-${order}`, true);
			vc.style.order = order;
		}
	});
};

const setupVideoPlay = function() {
	const playButton = document.querySelector(".pg-play-button");
	if (playButton) {
		playButton.addEventListener("click", () => {
			const iframe = document.querySelector(".pg-video-iframe");
			iframe.src =
				"https://www.youtube.com/embed/" +
				playButton.dataset.videoUrl +
				"?autoplay=1&rel=0";
			toggleClass(iframe, HIDDEN_CLASS, false);
		});
	}
};

const submitFeedback = () => {
	const feedbackInput = document.querySelector(".pga-activity-feedback-box");
	const feedback = feedbackInput.value.trim();

	const noticeEl = document.querySelector(".pga-activity-feedback-response");
	if (!feedback || feedback.length < 15) {
		noticeEl.textContent = "Feedback must be at least 15 characters.";
		toggleClass(noticeEl, HIDDEN_CLASS, false);
		return;
	}
	const feedbackResponse = (err, resp) => {
		const submitEl = document.querySelector(".pga-activity-feedback-submit");
		if (resp && resp.success) {
			noticeEl.textContent = "Thank you for your feedback!";
			toggleClass(submitEl, HIDDEN_CLASS, true);
			feedbackInput.value = "";
		} else {
			noticeEl.textContent = "Something went wrong, please try again later.";
		}
		toggleClass(noticeEl, HIDDEN_CLASS, false);
	};
	return runAjax(
		{
			endpoint: "/api/mailingList/submitFeedback",
			timeout: AJAX_TIMEOUT,
			payload: {feedback, role: getAudience(), activityURL: getActivityURL()},
		},
		feedbackResponse
	);
};

const setupVideoGrids = () => {
	const setupLoadMoreButton = containerEl => {
		const loadMoreButton = containerEl.querySelector(".pg-load-more");
		if (loadMoreButton) {
			loadMoreButton.addEventListener("click", () =>
				loadMoreActivities(containerEl)
			);
		}
	};

	const mainContainer = document.querySelector(".pg-video-grid-container-all");
	if (mainContainer) {
		const mainGrid = mainContainer.querySelector(".pg-videos-grid");
		if (mainGrid) {
			mainGrid.dataset.numShown = INITIALLY_SHOWN;
			doSort();
			showXActivities(mainContainer, true);
			setupLoadMoreButton(mainContainer);
		}
	}

	const closeContainer = document.querySelector(
		".pg-video-grid-container-close"
	);
	if (closeContainer) {
		const closeGrid = closeContainer.querySelector(".pg-videos-grid");
		if (closeGrid) {
			closeGrid.dataset.numShown = INITIALLY_SHOWN;
			setupLoadMoreButton(closeContainer);
		}
	}
};

const setupShortcuts = () => {
	const standardsLink = document.querySelector("#pga-standards-shortcut");
	const classroomLink = document.querySelector("#pga-classroom-shortcut");
	const topBarOuter = document.querySelector(".ba11-top-outer");
	if (standardsLink) {
		standardsLink.addEventListener("click", () => {
			const standardsEl = document.querySelector("#pga-standards-card");
			scrollToEl(standardsEl, true);
			toggleClass(topBarOuter, "--shrink", true);
		});
	}
	if (classroomLink) {
		classroomLink.addEventListener("click", () => {
			const classroomEl = document.querySelector(".pga-classroom-tips");
			scrollToEl(classroomEl, true);
			toggleClass(topBarOuter, "--shrink", true);
		});
	}
};

const setupPlayground = () => {
	getScrollbarWidth();

	setupFilters();
	const qs = QueryString.parse(window.location.search);
	if (qs && qs.audience) {
		if (ALLOWED_AUDIENCES.indexOf(qs.audience.toLowerCase()) > -1)
			setAudience(qs.audience.toLowerCase());
	}
	// Hide/Show stuff depending on audience
	updateViewsForAudience();
	setupSignupSticky();

	// Audience buttons
	ALLOWED_AUDIENCES.forEach(audience => {
		const buttons = document.querySelectorAll(`.pg-button-${audience}`);
		buttons.forEach(button => {
			button.addEventListener("click", () => setAudience(audience));
		});
	});

	setupVideoGrids();

	// Sort dropdown
	const sortDropdown = document.querySelector("#pg-select-sort");
	if (sortDropdown) {
		sortDropdown.addEventListener("change", () => {
			const newSortBy = sortDropdown.value;
			if (newSortBy !== sortBy) {
				sortBy = sortDropdown.value;
				doSort();
			}
		});
	}

	// Learning notes expandable
	const expandables = document.querySelectorAll(".pga-expandable");
	const expLink = document.querySelector("#pga-classroom-shortcut");
	if (expLink) {
		expLink.addEventListener("click", () =>
			toggleClass(
				document.querySelector(".pga-classroom-tips"),
				"--closed",
				false
			)
		);
	}

	expandables.forEach(exp => {
		const header = exp.querySelector(".pga-expandable-header");
		if (header) {
			header.addEventListener("click", () => toggleExpandable(exp));
		}
	});

	if (isPlaygroundActivityPage()) {
		setupRecVideos();
		setupShortcuts();
	}
	setupVideoPlay();

	// Mailing list email sign up
	const emailSubmitButton = document.querySelector(".pg-email-submit");
	const emailInput = document.querySelector(".pg-email-input");
	if (emailSubmitButton) {
		setupModalButton(emailSubmitButton, setupEmailModal);
	}
	if (emailInput && emailSubmitButton) {
		emailInput.addEventListener("change", evt => {
			const email = evt.target.value.trim();
			if (!email || !isValidEmail(email)) {
				emailSubmitButton.setAttribute(
					"data-modal-type",
					"playgroundSignupInvalidEmail"
				);
			} else {
				emailSubmitButton.setAttribute("data-modal-type", "playgroundSignup");
			}
		});
	}

	// Activity feedback form
	const feedbackSubmitButton = document.querySelector(
		".pga-activity-feedback-submit"
	);
	const feedbackInput = document.querySelector(".pga-activity-feedback-box");
	if (feedbackSubmitButton && feedbackInput) {
		feedbackSubmitButton.addEventListener("click", submitFeedback);
	}

	if (isPlaygroundActivityPage()) {
		const fbShareLinks = document.querySelectorAll("#share-link-fb");
		const twShareLinks = document.querySelectorAll("#share-link-tw");

		if (fbShareLinks && fbShareLinks.length) {
			fbShareLinks.forEach(shareLink => {
				shareLink.addEventListener("click", () => {
					ElevHelpers.sendDataLayerEvent("shareActivity", {site: "facebook"});
					return false;
				});
			});
		}

		if (twShareLinks && twShareLinks.length) {
			twShareLinks.forEach(shareLink => {
				shareLink.addEventListener("click", () => {
					ElevHelpers.sendDataLayerEvent("shareActivity", {site: "twitter"});
					return false;
				});
			});
		}
	}
};

const isPlaygroundActivityPage = () => {
	return window.location.pathname.match(/playground\/.+?/gi);
};

module.exports = {
	setupPlayground,
	isPlaygroundActivityPage,
};
